import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../../../lib/api";

// v1.23.0: edit the intro / promo script — on-screen title + lines and the voice-over, per slide. Header band and the
// intro's domain table stay machine-set. Save stores the override; the video changes only when you re-render.
const BANNED = [
  [/https?:\/\/|www\.|\.com\b|\.net\b|\.org\b/i, "URL"], [/exam\s*ray/i, "ExamRay"], [/ray\s*20/i, "RAY20"], [/\bsave\b/i, "save"],
  [/\bcoupon/i, "coupon"], [/\bdiscount/i, "discount"], [/\bpromo/i, "promo"], [/\bcode\b/i, "code"],
];
const PRICE = [/\$\s?\d|\bdollars?\b|\bprice\b|\bpricing\b|\bcheap\b|\bcost\b|\brefund/i, "price"];
const RATE = { intro: [2.5, 1.6], promo: [2.6, 0.8] };

function hits(text, which) {
  const t = String(text || "").replace(/exam\s+code/gi, "exam");
  const list = which === "promo" ? [...BANNED, PRICE] : BANNED;
  return list.filter(([re]) => re.test(t)).map(([, label]) => label);
}

export function LectureScriptEditor({ id, which, label, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [slides, setSlides] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/admin/certs/${id}/lecture-script/${which}`);
      setData(data);
      setSlides(data.effective.slides.map((s) => ({ ...s, linesText: s.lines.join("\n") })));
    } catch (e) { toast.error(errMsg(e), { duration: 10000 }); onClose(); }
  };
  useEffect(() => { load(); }, [id, which]); // eslint-disable-line react-hooks/exhaustive-deps
  const est = useMemo(() => {
    const words = slides.reduce((n, s) => n + String(s.narration || "").trim().split(/\s+/).filter(Boolean).length, 0);
    const [wps, pause] = RATE[which];
    return { words, seconds: Math.round((words / wps + pause * slides.length) * 10) / 10 };
  }, [slides, which]);
  const problems = slides.flatMap((s) => [
    ...hits(`${s.title}\n${s.linesText}`, which).map((w) => `slide ${s.id} on-screen: ${w}`),
    ...hits(s.narration, which).map((w) => `slide ${s.id} voice-over: ${w}`),
  ]);
  const [lo, hi] = data?.estimate?.bounds || [0, 0];
  const inRange = est.seconds >= lo && est.seconds <= hi;
  const set = (i, patch) => setSlides((cur) => cur.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const save = async () => {
    setBusy(true);
    try {
      const body = { slides: slides.map((s) => ({ id: s.id, title: s.title, lines: s.linesText.split("\n"), narration: s.narration })) };
      const { data } = await api.put(`/admin/certs/${id}/lecture-script/${which}`, body);
      toast.success(data.render_note, { duration: 8000 });
      if (!data.estimate.in_range) toast.warning(`Estimated ${data.estimate.seconds}s is outside ${lo}–${hi}s — the render audit will fail unless you trim or extend`, { duration: 10000 });
      onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e), { duration: 12000 }); }
    setBusy(false);
  };
  const reset = async () => {
    if (!window.confirm("Drop your edits and go back to the machine-written script?")) return;
    setBusy(true);
    try { await api.delete(`/admin/certs/${id}/lecture-script/${which}`); toast.success("Machine script restored — re-render to apply"); onSaved(); onClose(); }
    catch (e) { toast.error(errMsg(e), { duration: 10000 }); }
    setBusy(false);
  };
  if (!data) return <div className="rounded-md border border-zinc-200 bg-white p-4 text-xs text-zinc-500" data-testid="lecture-editor-loading">Loading script…</div>;
  return (
    <div className="rounded-md border border-sky-300 bg-white p-4 space-y-4" data-testid={`lecture-editor-${which}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Edit script · {label}</p>
          <p className="text-xs text-zinc-600">{data.override ? `Edited ${new Date(data.override.saved_at).toLocaleString()} — ` : "Machine-written — "}
            changes reach the video only when you press Re-render. The header band{which === "intro" ? " and the domain table" : ""} stay machine-set.</p>
        </div>
        <span className={`text-xs font-mono ${inRange ? "text-emerald-700" : "text-amber-700"}`} data-testid="lecture-editor-estimate">
          {est.words} words · ~{est.seconds}s (audit window {lo}–{hi}s)
        </span>
      </div>
      {problems.length > 0 && (
        <p className="text-xs text-red-700 rounded bg-red-50 border border-red-200 px-2 py-1" data-testid="lecture-editor-banned">
          Banned language — Save is refused: {problems.join(" · ")}
        </p>
      )}
      <ol className="space-y-3">
        {slides.map((s, i) => (
          <li key={s.id} className="rounded border border-zinc-200 p-3 grid gap-2 md:grid-cols-2" data-testid={`lecture-slide-${s.id}`}>
            <div className="space-y-1">
              <p className="text-[10px] font-mono uppercase text-zinc-400">Slide {i + 1} · on screen · {s.header}</p>
              <input value={s.title} onChange={(e) => set(i, { title: e.target.value })} data-testid={`lecture-slide-${s.id}-title`}
                     className="w-full rounded border border-zinc-300 px-2 py-1 text-sm font-semibold" placeholder="Slide title" />
              <textarea value={s.linesText} onChange={(e) => set(i, { linesText: e.target.value })} rows={Math.max(2, s.linesText.split("\n").length)}
                        data-testid={`lecture-slide-${s.id}-lines`} className="w-full rounded border border-zinc-300 px-2 py-1 text-xs font-mono" placeholder="One bullet per line" />
              {s.table && <p className="text-[10px] text-zinc-500">Domain table: machine-set from the blueprint ({s.table.length} rows)</p>}
            </div>
            <div className="space-y-1">
              <p className="text-[10px] font-mono uppercase text-zinc-400">Voice-over · {String(s.narration || "").trim().split(/\s+/).filter(Boolean).length} words</p>
              <textarea value={s.narration} onChange={(e) => set(i, { narration: e.target.value })} rows={5}
                        data-testid={`lecture-slide-${s.id}-narration`} className="w-full rounded border border-zinc-300 px-2 py-1 text-sm" />
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-2 justify-end">
        {data.override && <button onClick={reset} disabled={busy} data-testid="lecture-editor-reset-btn" className="px-3 py-1.5 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">Reset to machine script</button>}
        <button onClick={onClose} disabled={busy} data-testid="lecture-editor-cancel-btn" className="px-3 py-1.5 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-700 hover:bg-zinc-50">Cancel</button>
        <button onClick={save} disabled={busy || problems.length > 0} data-testid="lecture-editor-save-btn"
                className={`px-3 py-1.5 rounded-md text-xs font-semibold text-white ${busy || problems.length ? "bg-zinc-400" : "bg-zinc-900 hover:bg-zinc-700"}`}>
          {busy ? "Saving…" : "Save script"}
        </button>
      </div>
    </div>
  );
}
